// All energy values use abstract "energy units" (eu), never kWh — see the
// Minitools Briefing "Accuracy Trap": the game teaches concepts, not numbers.

export enum ApplianceId {
  DISHWASHER = 'dishwasher',
  EV_CHARGER = 'ev_charger',
  FRIDGE = 'fridge',
  HVAC = 'hvac',
  LAUNDRY = 'laundry',
  LIGHTS = 'lights',
  OVEN = 'oven',
  TV_CONSOLE = 'tv_console',
  WATER_HEATER = 'water_heater',
  WIFI = 'wifi',
}

export type FamilyMemberId = 'jojo' | 'riley' | 'sam';

export type FamilyMember = {
  /** one-line bio — who the player is keeping alive */
  bio: string;
  id: FamilyMemberId;
  /** letter drawn in the avatar badge */
  initial: string;
  name: string;
  role: string;
  /** scorecard verdict lines, chosen by how this member's asks went over the run */
  verdicts: {
    denied: string;
    granted: string;
    mixed: string;
    /** overrides the others when this member's personal disaster happened (Sam: house went dark, Riley: Wi-Fi cut, Jojo: food spoiled) */
    special: string;
  };
};

export enum ScenarioId {
  DEEP_FREEZE = 'deep_freeze',
  LOVELY_SPRING_DAY = 'lovely_spring_day',
  SUMMERTIME_STORMS = 'summertime_storms',
}

export enum ShopItemId {
  BATTERY = 'battery',
  EFFICIENCY_PACK = 'efficiency_pack',
  INSULATION = 'insulation',
  SOLAR_LARGE = 'solar_large',
  SOLAR_SMALL = 'solar_small',
}

export type Appliance = {
  /** eu per game hour while running */
  drainPerHour: number;
  /** essentials start powered and come back on their own when a dark house relights */
  essential?: boolean;
  id: ApplianceId;
  name: string;
  /** room highlighted in the dollhouse while this load runs */
  room: DollhouseRoom;
};

export type DollhouseRoom = 'bathroom' | 'bedroom' | 'garage' | 'kitchen' | 'living' | 'outside' | 'utility';

export type ShopItem = {
  description: string;
  /** mutually exclusive item — the engine honors only one of the pair, so the shop blocks owning both */
  excludes?: ShopItemId;
  id: ShopItemId;
  /** how many of this item can be bought (e.g. 2 battery units) */
  maxQuantity: number;
  name: string;
  price: number;
};

export type DifficultyTier = {
  budget: number;
  description: string;
  id: 'comfortable' | 'shoestring' | 'tight';
  name: string;
};

/** What answering a group-chat prompt does to the simulation */
export type ResponseEffect = {
  /** game minutes the started loads stay on; omit for indefinite (player can toggle off) */
  durationMinutes?: number;
  moodDelta?: number;
  /** narration bubble posted to the chat after choosing */
  reaction?: string;
  startLoads?: ApplianceId[];
  stopLoads?: ApplianceId[];
};

export type EventResponse = {
  effect: ResponseEffect;
  /** this answer gives the sender what they were hoping for — verdicts count it as granting the ask.
   *  Events with no granting response (acks, warnings, jokes) don't factor into verdicts. */
  grantsAsk?: boolean;
  label: string;
};

export type GameEvent = {
  /** minutes since game start (0 = 10:00 AM on day one) */
  atMinute: number;
  from: string;
  id: string;
  /** fire only while the house is warmer than this (°F) — for heat complaints */
  requiresIndoorAbove?: number;
  /** fire only while the house is colder than this (°F) — for cold complaints */
  requiresIndoorBelow?: number;
  /** fire only once the kids are back from school — for kid requests */
  requiresKidsHome?: boolean;
  /** fire only while this appliance is OFF — don't offer to start something already running */
  requiresLoadOff?: ApplianceId;
  /** fire only while this appliance is ON — the already-running variant of a paired prompt */
  requiresLoadOn?: ApplianceId;
  /** fire only if the player owns solar (true) or owns none (false) */
  requiresSolar?: boolean;
  responses: EventResponse[];
  /** events without responses are announcements (e.g. outage hits, kids home) */
  text: string;
};

export type Scenario = {
  description: string;
  events: GameEvent[];
  id: ScenarioId;
  /** game minute the kids get home — 0 means they're home all day (summer break) */
  kidsHomeFromMinute: number;
  name: string;
  /** outdoor °F by hour of day (24 entries, index 0 = midnight) */
  outdoorTempByHour: number[];
  /** multiplier 0–1 applied to solar generation by hour of day (24 entries) */
  solarFactorByHour: number[];
  /** day-two solar multiplier (sky after the storm clears, etc.) */
  solarFactorByHourDayTwo: number[];
};

export type PrepConfig = {
  difficultyId: DifficultyTier['id'];
  purchases: Partial<Record<ShopItemId, number>>;
  scenarioId: ScenarioId;
};

export type ActiveLoad = {
  applianceId: ApplianceId;
  /** absolute game minute when the load auto-stops; undefined = until toggled */
  untilMinute?: number;
};

export type ChatMessage = {
  atMinute: number;
  from: string;
  text: string;
};

/** one answered group-chat prompt — the verdict layer derives from these, never from chat text */
export type DecisionEntry = {
  atMinute: number;
  eventId: string;
  responseIndex: number;
};

export type GamePhase = 'playing' | 'prep' | 'scorecard';

export type GameState = {
  activeLoads: ActiveLoad[];
  /** eu currently stored */
  batteryEu: number;
  blackoutMinutes: number;
  chatLog: ChatMessage[];
  /** game minutes spent inside the comfort band while the grid is down */
  comfortMinutes: number;
  /** true while the battery is dead and the whole house is unpowered */
  dark: boolean;
  decisions: DecisionEntry[];
  endless: boolean;
  firedEventIds: string[];
  /** cumulative minutes the fridge has been unpowered (toggled off or house dark) */
  fridgeOffMinutes: number;
  gameMinutes: number;
  gridUp: boolean;
  indoorTempF: number;
  /** 0–100, the family's collective patience */
  mood: number;
  moodSamples: number;
  /** running sum of mood used for the end-of-game average */
  moodSum: number;
  /** minutes survived past the scheduled grid restore (endless mode) */
  overtimeMinutes: number;
  pendingEvent?: GameEvent;
  phase: GamePhase;
  prep: PrepConfig;
  speed: 1 | 2;
  /** cumulative minutes the Wi-Fi has been unpowered */
  wifiOffMinutes: number;
};

export type GameAction =
  | { responseIndex: number; type: 'respond' }
  | { type: 'restart' }
  | { speed: 1 | 2; type: 'setSpeed' }
  | { prep: PrepConfig; type: 'start' }
  | { type: 'startEndless' }
  | { deltaMinutes: number; type: 'tick' }
  | { applianceId: ApplianceId; type: 'toggleLoad' };

export type Score = {
  avgMood: number;
  batteryNeverEmpty: boolean;
  comfortPct: number;
  endingBatteryPct: number;
  /** hours survived past the scheduled restore, endless mode only */
  overtimeHours: number;
  /** score bonus for winning on a smaller budget (1 = no bonus) */
  tierMultiplier: number;
  title: string;
  total: number;
};
